// ===========================================
// Products Tab
// ===========================================
// Manage product catalog for selling individual items
// Phase 2 - Product Sales Feature

import { useState, useMemo, useRef, useCallback } from 'react';
import {
  Box,
  TextField,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Stack,
  InputAdornment,
  Typography,
  Button,
  Skeleton,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Inventory as InventoryIcon,
  Warning as LowStockIcon,
  Search as SearchIcon,
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  Image as ImageIcon,
  ReceiptLong as AddToInvoiceIcon,
} from '@mui/icons-material';
import FormModal from '../../common/FormModal';
import ConfirmDialog from '../../common/ConfirmDialog';
import AddProductToInvoiceDialog from './AddProductToInvoiceDialog';
import { useProductManagement, useProductCategories } from '../../../hooks/useProducts';
import {
  Product,
  formatProductPrice,
  getStockStatus,
  getStockStatusLabel,
  getStockStatusColor,
} from '../../../api/product.api';

// ===========================================
// COMPONENT
// ===========================================

export default function ProductsTab() {
  const {
    products,
    lowStockProducts,
    lowStockCount,
    isLoading,
    error,
    isCreating,
    isUpdating,
    isDeleting,
    isAdjustingInventory,
    isUploadingImage,
    isDeletingImage,
    create,
    update,
    remove,
    adjustInventory,
    uploadImage,
    deleteImage,
  } = useProductManagement();

  const { data: categories = [] } = useProductCategories();

  // Filter state
  const [hideInactive, setHideInactive] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');

  // Form state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    sku: '',
    price: '',
    categoryId: '' as string,
    imageUrl: '',
    trackInventory: false,
    quantity: '',
    lowStockAlert: '',
    isActive: true,
  });

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<Product | null>(null);

  // Inventory adjustment state
  const [inventoryDialogOpen, setInventoryDialogOpen] = useState(false);
  const [productForInventory, setProductForInventory] = useState<Product | null>(null);
  const [inventoryAdjustment, setInventoryAdjustment] = useState('');
  const [inventoryReason, setInventoryReason] = useState('');

  // Image upload state
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [imageUploadError, setImageUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Add to invoice state
  const [addToInvoiceDialogOpen, setAddToInvoiceDialogOpen] = useState(false);
  const [productForInvoice, setProductForInvoice] = useState<Product | null>(null);

  // Filter products
  const filteredProducts = useMemo(() => {
    let result = products;

    if (hideInactive) {
      result = result.filter((p) => p.isActive);
    }

    if (categoryFilter) {
      result = result.filter((p) => p.categoryId === categoryFilter);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (p) =>
          p.name.toLowerCase().includes(query) ||
          (p.description && p.description.toLowerCase().includes(query)) ||
          (p.sku && p.sku.toLowerCase().includes(query))
      );
    }

    return result;
  }, [products, hideInactive, categoryFilter, searchQuery]);

  // Helper: Can delete products that are inactive and have no invoice items
  const canDeleteProduct = (product: Product) =>
    !product.isActive && (!product._count || product._count.InvoiceItem === 0);

  // Image handlers
  const handleImageSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setImageUploadError('Invalid file type. Only JPEG, PNG, and WebP are allowed.');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setImageUploadError('File too large. Maximum size is 5MB.');
      return;
    }

    setImageUploadError(null);
    setSelectedImageFile(file);

    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setImagePreviewUrl(previewUrl);
  }, []);

  const handleRemoveSelectedImage = useCallback(() => {
    setSelectedImageFile(null);
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl);
    }
    setImagePreviewUrl(null);
    setImageUploadError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [imagePreviewUrl]);


  // Handlers
  const handleAdd = () => {
    setEditingProduct(null);
    setFormData({
      name: '',
      description: '',
      sku: '',
      price: '',
      categoryId: categories[0]?.id ?? '',
      imageUrl: '',
      trackInventory: false,
      quantity: '',
      lowStockAlert: '',
      isActive: true,
    });
    handleRemoveSelectedImage();
    setModalOpen(true);
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description || '',
      sku: product.sku || '',
      price: parseFloat(product.price).toString(),
      categoryId: product.categoryId || '',
      imageUrl: product.imageUrl || '',
      trackInventory: product.trackInventory,
      quantity: product.quantity?.toString() || '',
      lowStockAlert: product.lowStockAlert?.toString() || '',
      isActive: product.isActive,
    });
    handleRemoveSelectedImage();
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    handleRemoveSelectedImage();
  };

  const handleDelete = (product: Product) => {
    setProductToDelete(product);
    setDeleteDialogOpen(true);
  };

  const handleOpenInventory = (product: Product) => {
    setProductForInventory(product);
    setInventoryAdjustment('');
    setInventoryReason('');
    setInventoryDialogOpen(true);
  };

  const handleSave = async () => {
    const data = {
      name: formData.name,
      description: formData.description || undefined,
      sku: formData.sku || undefined,
      price: parseFloat(formData.price),
      categoryId: formData.categoryId || null,
      imageUrl: formData.imageUrl || undefined,
      trackInventory: formData.trackInventory,
      quantity: formData.trackInventory && formData.quantity ? parseInt(formData.quantity) : undefined,
      lowStockAlert:
        formData.trackInventory && formData.lowStockAlert
          ? parseInt(formData.lowStockAlert)
          : undefined,
      isActive: formData.isActive,
    };

    try {
      let productId: string;

      if (editingProduct) {
        await update(editingProduct.id, data);
        productId = editingProduct.id;
      } else {
        const newProduct = await create(data);
        productId = newProduct.id;
      }

      // Upload image if a new file was selected
      if (selectedImageFile) {
        await uploadImage(productId, selectedImageFile);
      }

      handleCloseModal();
    } catch (err) {
      // Error handling is done by the hooks
      console.error('Error saving product:', err);
    }
  };

  const handleDeleteExistingImage = async () => {
    if (editingProduct?.imageUrl) {
      try {
        await deleteImage(editingProduct.id);
        setFormData((prev) => ({ ...prev, imageUrl: '' }));
      } catch (err) {
        console.error('Error deleting image:', err);
      }
    }
  };

  const handleConfirmDelete = async () => {
    if (productToDelete) {
      await remove(productToDelete.id);
      setDeleteDialogOpen(false);
      setProductToDelete(null);
    }
  };

  const handleConfirmInventory = async () => {
    if (productForInventory && inventoryAdjustment) {
      await adjustInventory(productForInventory.id, {
        adjustment: parseInt(inventoryAdjustment),
        reason: inventoryReason || undefined,
      });
      setInventoryDialogOpen(false);
      setProductForInventory(null);
    }
  };

  // Helper: Render stock status
  const renderStockStatus = (product: Product) => {
    const status = getStockStatus(product);
    const color = getStockStatusColor(status);
    const label = getStockStatusLabel(status);

    if (status === 'not_tracked') {
      return (
        <Typography variant="caption" color="text.secondary">
          Not tracked
        </Typography>
      );
    }

    return (
      <Chip
        label={`${product.quantity ?? 0} - ${label}`}
        size="small"
        color={color}
        variant={status === 'low_stock' || status === 'out_of_stock' ? 'filled' : 'outlined'}
      />
    );
  };

  // Helper: Render usage count
  const renderUsageCount = (product: Product) => {
    const count = product._count?.InvoiceItem ?? 0;
    return count > 0 ? (
      <Chip label={`${count} invoices`} size="small" variant="outlined" />
    ) : (
      <Typography variant="caption" color="text.secondary">
        Never
      </Typography>
    );
  };

  // Render loading state
  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={300} />
      </Box>
    );
  }

  // Render error state
  if (error) {
    return <Alert severity="error">Failed to load products: {(error as Error).message}</Alert>;
  }

  return (
    <Box>
      {/* Low stock alert */}
      {lowStockCount > 0 && (
        <Alert
          severity="warning"
          icon={<LowStockIcon />}
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => setCategoryFilter('')}>
              View All
            </Button>
          }
        >
          {lowStockCount} product{lowStockCount > 1 ? 's' : ''} with low stock:{' '}
          {lowStockProducts
            .slice(0, 3)
            .map((p) => p.name)
            .join(', ')}
          {lowStockCount > 3 && ` and ${lowStockCount - 3} more`}
        </Alert>
      )}

      {/* Toolbar */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ mb: 2 }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            size="small"
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 200 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              label="Category"
            >
              <MenuItem value="">All Categories</MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {cat.color && (
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: cat.color,
                          flexShrink: 0,
                        }}
                      />
                    )}
                    {cat.name}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={hideInactive}
                onChange={(e) => setHideInactive(e.target.checked)}
                size="small"
              />
            }
            label="Active only"
          />
        </Stack>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
          Add Product
        </Button>
      </Stack>

      {/* Products table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 200 }}>Product</TableCell>
              <TableCell sx={{ minWidth: 130 }}>Category</TableCell>
              <TableCell sx={{ minWidth: 100 }} align="right">Price</TableCell>
              <TableCell sx={{ minWidth: 120 }} align="center">Stock</TableCell>
              <TableCell sx={{ minWidth: 80 }} align="center">Status</TableCell>
              <TableCell sx={{ minWidth: 80 }} align="center">Used</TableCell>
              <TableCell sx={{ minWidth: 140 }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredProducts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No products found. Click &apos;Add Product&apos; to create one.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredProducts.map((product) => (
                <TableRow key={product.id} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar
                        src={product.imageThumbnails?.sm || product.imageUrl || undefined}
                        variant="rounded"
                        sx={{ width: 40, height: 40, bgcolor: 'grey.200' }}
                      >
                        <ImageIcon fontSize="small" sx={{ color: 'grey.500' }} />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {product.name}
                        </Typography>
                        {product.sku && (
                          <Typography variant="caption" color="text.secondary">
                            SKU: {product.sku}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {product.ProductCategory ? (
                      <Chip
                        label={product.ProductCategory.name}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderColor: product.ProductCategory.color || undefined,
                          color: product.ProductCategory.color || undefined,
                        }}
                      />
                    ) : (
                      <Chip label="Uncategorised" size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell align="right">{formatProductPrice(product.price)}</TableCell>
                  <TableCell align="center">{renderStockStatus(product)}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={product.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={product.isActive ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">{renderUsageCount(product)}</TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEdit(product)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {product.isActive && (
                        <Tooltip title="Add to Invoice">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setProductForInvoice(product);
                              setAddToInvoiceDialogOpen(true);
                            }}
                          >
                            <AddToInvoiceIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {product.trackInventory && (
                        <Tooltip title="Adjust Inventory">
                          <IconButton size="small" onClick={() => handleOpenInventory(product)}>
                            <InventoryIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title={canDeleteProduct(product) ? 'Delete' : 'Deactivate to delete'}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(product)}
                            disabled={!canDeleteProduct(product)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Modal */}
      <FormModal
        open={modalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSave}
        title={editingProduct ? 'Edit Product' : 'Add Product'}
        submitLabel={editingProduct ? 'Save Changes' : 'Create Product'}
        loading={isCreating || isUpdating || isUploadingImage}
        maxWidth="sm"
      >
        <Stack spacing={2.5}>
          <TextField
            label="Product Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            fullWidth
            helperText="e.g., Piano Method Book - Grade 2"
          />
          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            multiline
            rows={2}
            fullWidth
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="SKU"
              value={formData.sku}
              onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
              fullWidth
              helperText="Optional stock keeping unit"
            />
            <TextField
              label="Price"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: e.target.value })}
              type="number"
              required
              fullWidth
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
            />
          </Stack>
          <FormControl fullWidth>
            <InputLabel>Category</InputLabel>
            <Select
              value={formData.categoryId}
              onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
              label="Category"
            >
              <MenuItem value="">Uncategorised</MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {cat.color && (
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: cat.color,
                          flexShrink: 0,
                        }}
                      />
                    )}
                    {cat.name}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Product Image Upload */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Product Image
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleImageSelect}
              style={{ display: 'none' }}
              id="product-image-upload"
            />

            {/* Show existing image (if editing and has image) */}
            {editingProduct?.imageUrl && !selectedImageFile && !imagePreviewUrl && formData.imageUrl && (
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                <Avatar
                  src={editingProduct.imageThumbnails?.md || editingProduct.imageUrl}
                  variant="rounded"
                  sx={{ width: 80, height: 80 }}
                >
                  <ImageIcon />
                </Avatar>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Current image
                  </Typography>
                  <Button
                    size="small"
                    color="error"
                    onClick={handleDeleteExistingImage}
                    disabled={isDeletingImage}
                    startIcon={isDeletingImage ? <CircularProgress size={14} /> : <DeleteIcon />}
                  >
                    Remove
                  </Button>
                </Box>
              </Stack>
            )}

            {/* Show preview of selected new image */}
            {imagePreviewUrl && (
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                <Avatar
                  src={imagePreviewUrl}
                  variant="rounded"
                  sx={{ width: 80, height: 80 }}
                >
                  <ImageIcon />
                </Avatar>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    New image: {selectedImageFile?.name}
                  </Typography>
                  <Button
                    size="small"
                    color="error"
                    onClick={handleRemoveSelectedImage}
                    startIcon={<CloseIcon />}
                  >
                    Remove
                  </Button>
                </Box>
              </Stack>
            )}

            {imageUploadError && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {imageUploadError}
              </Alert>
            )}

            {/* Upload button - show when no image selected */}
            {!imagePreviewUrl && (
              <label htmlFor="product-image-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<UploadIcon />}
                  size="small"
                >
                  {editingProduct?.imageUrl && formData.imageUrl ? 'Replace Image' : 'Upload Image'}
                </Button>
              </label>
            )}

            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              JPEG, PNG, or WebP. Max 5MB. Optional.
            </Typography>
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={formData.trackInventory}
                onChange={(e) => setFormData({ ...formData, trackInventory: e.target.checked })}
              />
            }
            label="Track Inventory"
          />
          {formData.trackInventory && (
            <Stack direction="row" spacing={2}>
              <TextField
                label="Quantity"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                type="number"
                fullWidth
              />
              <TextField
                label="Low Stock Alert"
                value={formData.lowStockAlert}
                onChange={(e) => setFormData({ ...formData, lowStockAlert: e.target.value })}
                type="number"
                fullWidth
                helperText="Alert when stock is at or below"
              />
            </Stack>
          )}
          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
            }
            label="Active"
          />
        </Stack>
      </FormModal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onCancel={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Product"
        message={`Are you sure you want to delete "${productToDelete?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        confirmColor="error"
        loading={isDeleting}
      />

      {/* Inventory Adjustment Dialog */}
      <FormModal
        open={inventoryDialogOpen}
        onClose={() => setInventoryDialogOpen(false)}
        onSubmit={handleConfirmInventory}
        title="Adjust Inventory"
        submitLabel="Adjust"
        loading={isAdjustingInventory}
        maxWidth="xs"
      >
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Current stock for <strong>{productForInventory?.name}</strong>:{' '}
            <strong>{productForInventory?.quantity ?? 0}</strong>
          </Typography>
          <TextField
            label="Adjustment"
            value={inventoryAdjustment}
            onChange={(e) => setInventoryAdjustment(e.target.value)}
            type="number"
            required
            fullWidth
            helperText="Positive to add, negative to remove"
          />
          <TextField
            label="Reason"
            value={inventoryReason}
            onChange={(e) => setInventoryReason(e.target.value)}
            fullWidth
            helperText="Optional reason for adjustment"
          />
        </Stack>
      </FormModal>

      {/* Add Product to Invoice Dialog */}
      <AddProductToInvoiceDialog
        open={addToInvoiceDialogOpen}
        product={productForInvoice}
        onClose={() => {
          setAddToInvoiceDialogOpen(false);
          setProductForInvoice(null);
        }}
        onSuccess={() => {
          setAddToInvoiceDialogOpen(false);
          setProductForInvoice(null);
        }}
      />
    </Box>
  );
}
